import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Archive, Grid2X2, Plus, RotateCcw, SlidersHorizontal, Table2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import { FilterBar, FilterField } from '@/Components/data/FilterBar';
import { SearchField } from '@/Components/forms/SearchField';
import { EmptyState } from '@/Components/data/EmptyState';
import { CountBadge, StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import { DataTableColumnsMenu } from '@/Components/data/DataTableColumnsMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/Components/ui/DropdownMenu';
import produtoService, { type Produto } from '@/services/produto';
import tipoProdutoService, { getTipoProdutoUuid, type TipoProduto } from '@/services/tipoProduto';

type ViewMode = 'table' | 'grid';
type ProdutoColumnId = 'codigo' | 'erp' | 'barras' | 'descricao' | 'tipo' | 'unidade' | 'estoque' | 'status';

type ProdutoColumn = {
  id: ProdutoColumnId;
  label: string;
  render: (produto: Produto) => ReactNode;
  primary?: boolean;
  hideOnMobile?: boolean;
  align?: 'left' | 'center' | 'right';
};

const columns: ProdutoColumn[] = [
  { id: 'codigo', label: 'Código', render: (item) => item.codigo || '-' },
  { id: 'erp', label: 'Cód. ERP', hideOnMobile: true, render: (item) => item.codigo_erp || '-' },
  { id: 'barras', label: 'Cód. Barras', hideOnMobile: true, render: (item) => item.codigo_barras || '-' },
  { id: 'descricao', label: 'Descrição', primary: true, render: (item) => item.descricao },
  { id: 'tipo', label: 'Tipo', render: (item) => item.tipo_produto_descricao || '-' },
  { id: 'unidade', label: 'Unidade', align: 'center', render: (item) => item.unidade || '-' },
  { id: 'estoque', label: 'Estoque', align: 'right', render: (item) => (item.estoque_atual ?? 0).toFixed(3) },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (item) => (
      <StatusBadge label={item.ativo ? 'Ativo' : 'Inativo'} tone={item.ativo ? 'success' : 'danger'} />
    ),
  },
];

export default function Produtos() {
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [tiposProduto, setTiposProduto] = useState<TipoProduto[]>([]);
  const [loading, setLoading] = useState(false);
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');
  const [draftTipo, setDraftTipo] = useState('');
  const [tipo, setTipo] = useState('');
  const [draftAtivo, setDraftAtivo] = useState('');
  const [ativo, setAtivo] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [compactActions, setCompactActions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<ProdutoColumnId, boolean>>(
    () => Object.fromEntries(columns.map((column) => [column.id, true])) as Record<ProdutoColumnId, boolean>,
  );
  const [columnOrder, setColumnOrder] = useState<ProdutoColumnId[]>(() => columns.map((column) => column.id));

  const carregarProdutos = async () => {
    setLoading(true);
    try {
      const data = await produtoService.listar({
        tipo_produto: tipo || undefined,
        ativo: ativo === '' ? undefined : ativo === 'true',
        search: search || undefined,
      });
      setProdutos(data);
    } catch (error) {
      console.error('Erro ao carregar produtos', error);
      setProdutos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    tipoProdutoService.listar().then(setTiposProduto).catch(console.error);
  }, []);

  useEffect(() => {
    carregarProdutos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, ativo, search]);

  const activeColumns = useMemo(
    () => columnOrder
      .map((id) => columns.find((column) => column.id === id))
      .filter((column): column is ProdutoColumn => Boolean(column && visibleColumns[column.id])),
    [columnOrder, visibleColumns],
  );
  const tiposProdutoValidos = useMemo(() => tiposProduto.filter((item) => getTipoProdutoUuid(item)), [tiposProduto]);

  const lastPage = Math.max(1, Math.ceil(produtos.length / perPage));
  const currentPage = Math.min(page, lastPage);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return produtos.slice(start, start + perPage);
  }, [currentPage, perPage, produtos]);
  const from = produtos.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = produtos.length === 0 ? 0 : Math.min(from + pageData.length - 1, produtos.length);

  function submitFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    setSearch(draftSearch.trim());
    setTipo(draftTipo);
    setAtivo(draftAtivo);
  }

  function clearFilters() {
    setDraftSearch('');
    setSearch('');
    setDraftTipo('');
    setTipo('');
    setDraftAtivo('');
    setAtivo('');
    setPage(1);
  }

  const deletar = async (item: Produto) => {
    await produtoService.excluir(item.id);
    await carregarProdutos();
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Archive size={21} strokeWidth={2} />}
        title="Produtos"
        description="Cadastro de produtos, insumos e itens controlados pela industrialização."
        actions={
          <Button type="button" onClick={() => navigate('/producao/produtos/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Produto
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions nx-listing-toolbar">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost">
                    <Grid2X2 size={16} />
                    Visualização
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Tipo de visualização</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
                    <DropdownMenuRadioItem value="table">
                      <Table2 size={15} /> Tabela
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="grid">
                      <Grid2X2 size={15} /> Grade
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <DataTableColumnsMenu
                options={columns}
                columnOrder={columnOrder}
                visibility={visibleColumns}
                onToggle={(id, visible) => setVisibleColumns((current) => ({ ...current, [id]: visible }))}
                onReorder={(order) => setColumnOrder([...order])}
                onReset={() => {
                  setVisibleColumns(Object.fromEntries(columns.map((column) => [column.id, true])) as Record<ProdutoColumnId, boolean>);
                  setColumnOrder(columns.map((column) => column.id));
                }}
              />

              <Button
                type="button"
                variant="ghost"
                aria-pressed={compactActions}
                onClick={() => setCompactActions((value) => !value)}
              >
                <SlidersHorizontal size={15} />
                Modo de Ações
              </Button>

              <CountBadge count={`${produtos.length} registros`} />
            </div>
          }
        />

        <FilterBar
          onSubmit={submitFilters}
          search={
            <SearchField
              label="Buscar por código ou descrição"
              placeholder="Buscar código ou descrição..."
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
            />
          }
          filters={
            <>
              <FilterField label="Tipo" className="nx-sngpc-filter-field">
                <Select filter value={draftTipo} onChange={(event) => setDraftTipo(event.target.value)}>
                  <option value="">Todos</option>
                {tiposProdutoValidos.map((item) => (
                  <option key={item.id} value={getTipoProdutoUuid(item) || ''}>{item.descricao}</option>
                ))}
              </Select>
              </FilterField>
              <FilterField label="Status" className="nx-sngpc-filter-field">
                <Select filter value={draftAtivo} onChange={(event) => setDraftAtivo(event.target.value)}>
                  <option value="">Todos</option>
                  <option value="true">Ativos</option>
                  <option value="false">Inativos</option>
                </Select>
              </FilterField>
              <FilterField label="Por página">
                <Select
                  filter
                  value={perPage}
                  onChange={(event) => {
                    setPage(1);
                    setPerPage(Number(event.target.value));
                  }}
                >
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </Select>
              </FilterField>
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <RotateCcw size={15} aria-hidden />
                Limpar
              </Button>
              <Button type="submit">Filtrar</Button>
            </>
          }
        />

        {viewMode === 'table' ? (
          <div className="nx-table-wrap">
            <table className={`nx-table nx-sngpc-table ${compactActions ? 'nx-table--compact' : ''}`}>
              <thead>
                <tr>
                  {activeColumns.map((column) => (
                    <th
                      key={column.id}
                      className={`nx-cell-${column.align ?? 'left'} ${column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''}`}
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="nx-table-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-table-loading">Carregando registros...</td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-sngpc-empty-cell">
                      Nenhum produto encontrado.
                    </td>
                  </tr>
                ) : (
                  pageData.map((item) => (
                    <tr key={item.id}>
                      {activeColumns.map((column) => (
                        <td
                          key={column.id}
                          className={`nx-cell-${column.align ?? 'left'} ${column.primary ? 'nx-table-name' : ''} ${column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''}`}
                        >
                          {column.render(item)}
                        </td>
                      ))}
                      <td className="nx-table-actions">
                        <TableActions
                          onEdit={() => navigate(`/producao/produtos/${item.id}/editar`)}
                          onDelete={() => deletar(item)}
                          deleteTitle="Excluir produto"
                          deleteDescription={`Tem certeza de que deseja excluir o produto "${item.descricao}"? Esta ação não poderá ser desfeita.`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="nx-sngpc-grid-loading">Carregando registros...</div>
        ) : pageData.length === 0 ? (
          <EmptyState message="Nenhum produto encontrado." />
        ) : (
          <div className="nx-sngpc-record-grid">
            {pageData.map((item) => (
              <article className="nx-sngpc-record" key={item.id}>
                {activeColumns.map((column) => (
                  <div key={column.id} className="nx-sngpc-record-field">
                    <span>{column.label}</span>
                    <strong>{column.render(item)}</strong>
                  </div>
                ))}
                <div className="nx-sngpc-record-actions">
                  <TableActions
                    onEdit={() => navigate(`/producao/produtos/${item.id}/editar`)}
                    onDelete={() => deletar(item)}
                    deleteTitle="Excluir produto"
                    deleteDescription={`Tem certeza de que deseja excluir o produto "${item.descricao}"? Esta ação não poderá ser desfeita.`}
                  />
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="nx-pagination">
          <span>{from}-{to} de {produtos.length}</span>
          <div className="nx-pagination-pages">
            <Button type="button" variant="ghost" disabled={currentPage <= 1} onClick={() => setPage((value) => value - 1)}>
              Anterior
            </Button>
            <Button type="button" variant="ghost" disabled={currentPage >= lastPage} onClick={() => setPage((value) => value + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
